<script setup lang="ts">
import { inject, computed, onMounted } from 'vue'
import { useIncidentStore } from '@/stores/incident'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { useFormsStore } from '@/stores/forms'
import { useSyncStore } from '@/stores/sync'
import FormShell from '@/components/forms/FormShell.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import { FormType, OverheadRole } from '@/types'

const incidentStore = useIncidentStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const formsStore = useFormsStore()
const syncStore = useSyncStore()
const incidentId = inject<string>('incidentId', '')

const FORM_TYPE = FormType.ICS201

const formData = computed(() => formsStore.getFormData(FORM_TYPE))
const incident = computed(() => incidentStore.currentIncident)

function namesForRole(role: string): string {
  return teamsStore.userOps
    .filter((uo) => uo.role === role && uo.active)
    .map((uo) => personnelStore.roster.find((p) => p.personnel_id === uo.personnel_id))
    .filter((p) => p !== undefined)
    .map((p) => `${p.last}, ${p.first}`)
    .join('; ')
}

const icName = computed(() => namesForRole(OverheadRole.IC) || '—')
const opsChief = computed(() => namesForRole(OverheadRole.OpsChief) || '—')
const plansChief = computed(() => namesForRole(OverheadRole.PlansChief) || '—')
const logistics = computed(() => namesForRole(OverheadRole.Logistics) || '—')

function field(key: string): string {
  const v = formData.value[key]
  return v != null ? String(v) : ''
}

async function save(fieldPath: string, event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, FORM_TYPE, fieldPath, value)
  } finally {
    syncStore.pendingWrites--
  }
}

async function saveSituationSummary(event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await incidentStore.updateIncident(incidentId, { situation_summary: value })
  } finally {
    syncStore.pendingWrites--
  }
}

onMounted(async () => {
  if (!incidentId) return
  const existing = formsStore.getFormData(FORM_TYPE)
  if (!existing['date_prepared']) {
    const now = new Date()
    await formsStore.saveFormField(incidentId, FORM_TYPE, 'date_prepared', now.toLocaleDateString('en-CA'))
    await formsStore.saveFormField(incidentId, FORM_TYPE, 'time_prepared', now.toTimeString().slice(0, 5))
  }
})
</script>

<template>
  <FormShell
    :form-type="FORM_TYPE"
    title="ICS 201 · Incident Briefing"
    back-route="incident-forms"
    back-label="Forms"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 201 · Incident Briefing</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Incident Briefing</h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Incident overview, command staff, situation summary, and map reference.</p>
      </div>

      <!-- Incident Information (read-only) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Incident Information</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Name</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ incident?.incident_name ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Number</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ incident?.incident_number ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Start Date</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ incident?.start_date ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Start Time</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ incident?.start_time ?? '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Command & General Staff (read-only, live from roster) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Command & General Staff</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Live from roster</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Commander</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ icName }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operations Section Chief</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ opsChief }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Plans Section Chief</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ plansChief }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Logistics Section Chief</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ logistics }}</p>
          </div>
        </div>
      </div>

      <!-- Situation Summary (writes to incident.situation_summary) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Situation Summary</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Shared with incident</span>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="incident?.situation_summary ?? ''"
            placeholder="Current situation summary for this incident…"
            :rows="5"
            @blur="saveSituationSummary($event)"
          />
        </div>
      </div>

      <!-- Map / Sketch Reference -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Map / Sketch Reference</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea
            :model-value="field('map_sketch')"
            placeholder="Map name, CalTopo link, sketch description, or attached reference…"
            :rows="3"
            @blur="save('map_sketch', $event)"
          />
        </div>
      </div>

      <!-- Date & Time Prepared -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Date & Time Prepared</h3>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Date</label>
            <FosInput
              type="date"
              :model-value="field('date_prepared')"
              @blur="save('date_prepared', $event)"
            />
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Time</label>
            <FosInput
              type="time"
              :model-value="field('time_prepared')"
              @blur="save('time_prepared', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Prepared By -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Name</label>
            <FosInput
              :model-value="field('prepared_by_name')"
              placeholder="Full name…"
              @blur="save('prepared_by_name', $event)"
            />
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Position / Title</label>
            <FosInput
              :model-value="field('prepared_by_position')"
              placeholder="Position or title…"
              @blur="save('prepared_by_position', $event)"
            />
          </div>
        </div>
      </div>
    </div>
  </FormShell>
</template>
